import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { AppearanceSettings } from '../models/appearance-settings.model';
import { SecuritySettingsDto, TeamMfaDto } from '../models/security-settings.model';
import { ChangePasswordDto } from '../models/change-password.model';
import { NotificationSettingsDto } from '../models/notification-settings.model';
import { OperatingHoursDto } from '../models/operating-hours.model';
import {
  ClinicSubscriptionDto,
  SubscriptionPackageDto,
} from '../models/subscription-settings.model';
import { BillingSettingsDto } from '../models/billing-settings.model';
import { PayFastCheckout, SubscriptionPaymentStatus } from '../models/subscription-payment.model';
import { Observable } from 'rxjs';
import { ClinicProfileDto } from '../models/clinic-profile.dto';
import { ConsultationPricingItemDto } from '../models/consultation-pricing-item.model';
import { ClinicInvoice } from '../models/clinic-invoice.model';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private baseUrl = environment.APP_BASE_URL + '/api/staff-settings';

  constructor(private http: HttpClient) {}

  getAppearanceSettings(): Observable<AppearanceSettings> {
    return this.http.get<AppearanceSettings>(`${this.baseUrl}/appearance`);
  }

  updateAppearanceSettings(settings: AppearanceSettings): Observable<AppearanceSettings> {
    return this.http.put<AppearanceSettings>(`${this.baseUrl}/appearance`, settings);
  }

  getSecuritySettings(): Observable<SecuritySettingsDto> {
    return this.http.get<SecuritySettingsDto>(`${this.baseUrl}/security`);
  }

  updateSecuritySettings(settings: SecuritySettingsDto): Observable<SecuritySettingsDto> {
    return this.http.put<SecuritySettingsDto>(`${this.baseUrl}/security`, settings);
  }

  getTeamMfaSetting(): Observable<TeamMfaDto> {
    return this.http.get<TeamMfaDto>(`${this.baseUrl}/security/team-mfa`);
  }

  updateTeamMfaSetting(dto: TeamMfaDto): Observable<TeamMfaDto> {
    return this.http.put<TeamMfaDto>(`${this.baseUrl}/security/team-mfa`, dto);
  }

  changePassword(changePasswordDto: ChangePasswordDto): Observable<boolean> {
    return this.http.post<boolean>(`${this.baseUrl}/security/change-password`, changePasswordDto);
  }

  getNotificationSettings(): Observable<NotificationSettingsDto> {
    return this.http.get<NotificationSettingsDto>(`${this.baseUrl}/notifications`);
  }

  updateNotificationSettings(
    settings: NotificationSettingsDto,
  ): Observable<NotificationSettingsDto> {
    return this.http.put<NotificationSettingsDto>(`${this.baseUrl}/notifications`, settings);
  }

  getOperatingHours(): Observable<OperatingHoursDto> {
    return this.http.get<OperatingHoursDto>(`${this.baseUrl}/operating-hours`);
  }

  updateOperatingHours(settings: OperatingHoursDto): Observable<OperatingHoursDto> {
    return this.http.put<OperatingHoursDto>(`${this.baseUrl}/operating-hours`, settings);
  }

  getSubscriptionPackages(): Observable<SubscriptionPackageDto[]> {
    return this.http.get<SubscriptionPackageDto[]>(`${this.baseUrl}/subscription/packages`);
  }

  getSubscriptionPackageFeatures(packageId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/subscription/packages/${packageId}/features`);
  }

  getClinicSubscription(): Observable<ClinicSubscriptionDto> {
    return this.http.get<ClinicSubscriptionDto>(`${this.baseUrl}/subscription`);
  }

  saveClinicSubscription(packageId: string): Observable<ClinicSubscriptionDto> {
    return this.http.put<ClinicSubscriptionDto>(`${this.baseUrl}/subscription`, null, {
      params: { packageId },
    });
  }

  getClinicSubscriptionPaymentStatus(): Observable<SubscriptionPaymentStatus> {
    return this.http.get<SubscriptionPaymentStatus>(
      `${environment.APP_BASE_URL}/api/payments/payfast/status`,
    );
  }

  createClinicPayFastCheckout(): Observable<PayFastCheckout> {
    return this.http.post<PayFastCheckout>(
      `${environment.APP_BASE_URL}/api/payments/payfast/checkout`,
      {},
    );
  }

  getClinicInvoices(clinicId: string): Observable<ClinicInvoice[]> {
    return this.http.get<ClinicInvoice[]>(
      `${environment.APP_BASE_URL}/api/clinic-invoices/${clinicId}`,
    );
  }

  getClinicBilling(): Observable<BillingSettingsDto> {
    return this.http.get<BillingSettingsDto>(`${this.baseUrl}/billing`);
  }

  saveClinicBilling(settings: BillingSettingsDto): Observable<BillingSettingsDto> {
    return this.http.put<BillingSettingsDto>(`${this.baseUrl}/billing`, settings);
  }

  getConsultationPricingItems(): Observable<ConsultationPricingItemDto[]> {
    return this.http.get<ConsultationPricingItemDto[]>(`${this.baseUrl}/consultation-pricing`);
  }

  saveConsultationPricingItems(
    items: ConsultationPricingItemDto[],
  ): Observable<ConsultationPricingItemDto[]> {
    return this.http.put<ConsultationPricingItemDto[]>(
      `${this.baseUrl}/consultation-pricing`,
      items,
    );
  }

  getClinicProfile(): Observable<ClinicProfileDto> {
    return this.http.get<ClinicProfileDto>(`${this.baseUrl}/clinic-profile`);
  }

  updateClinicProfile(clinicProfile: ClinicProfileDto): Observable<ClinicProfileDto> {
    return this.http.put<ClinicProfileDto>(`${this.baseUrl}/clinic-profile`, clinicProfile);
  }

  deactivateClinic(): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/clinic-profile/deactivate`, null);
  }
}
